/**
 * Typed navigation registry helpers for monograph demo shells.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { adjacentMonographPanelId } from "./navigation.js";
import type { MonographDemoNavEntry } from "./types.js";

export type MonographNavEntryList<PanelId extends string = string> = readonly [
  MonographDemoNavEntry<PanelId>,
  ...MonographDemoNavEntry<PanelId>[],
];

export interface DefineMonographNavigationOptions<PanelId extends string> {
  readonly defaultPanelId?: PanelId;
}

export interface MonographNavigationRegistry<
  PanelId extends string,
  Entries extends readonly MonographDemoNavEntry<PanelId>[],
> {
  readonly entries: Entries;
  readonly defaultPanelId: PanelId;
  readonly ids: readonly PanelId[];
  readonly isPanelId: (value: string) => value is PanelId;
  readonly adjacentPanelId: (id: PanelId, delta: -1 | 1) => PanelId;
}

export type MonographNavigationEntry<Registry> = Registry extends {
  readonly entries: readonly (infer Entry)[];
}
  ? Entry
  : never;

export type MonographNavigationPanelId<Registry> =
  MonographNavigationEntry<Registry> extends { readonly id: infer PanelId extends string }
    ? PanelId
    : never;

export function defineMonographNavigation<const Entries extends MonographNavEntryList>(
  entries: Entries,
  options: DefineMonographNavigationOptions<Entries[number]["id"]> = {},
): MonographNavigationRegistry<Entries[number]["id"], Entries> {
  const ids = entries.map((entry) => entry.id) as readonly Entries[number]["id"][];
  const firstEntry = entries[0] as MonographDemoNavEntry<Entries[number]["id"]> | undefined;
  if (firstEntry === undefined) {
    throw new Error("defineMonographNavigation requires at least one nav entry");
  }
  const firstId = firstEntry.id;

  const defaultPanelId = options.defaultPanelId ?? firstId;
  const idSet = new Set<Entries[number]["id"]>(ids);

  return {
    entries,
    defaultPanelId,
    ids,
    isPanelId(value: string): value is Entries[number]["id"] {
      return idSet.has(value);
    },
    adjacentPanelId(id: Entries[number]["id"], delta: -1 | 1): Entries[number]["id"] {
      return adjacentMonographPanelId(ids, id, delta);
    },
  };
}
